import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ExternalLink, Filter } from "lucide-react";

const STATUS_STYLES = {
  new: "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30",
  contacted: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  converted: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  archived: "bg-gray-500/10 text-gray-400 border-gray-700",
};

export default function TailorMadeList() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/tailor-made-requests")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Tailor-Made</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Requests de programas custom</h1>
        <p className="mt-2 text-gray-400">Convierte requests en programas reales publicables.</p>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-500" aria-hidden="true" />
          {["all", "new", "contacted", "converted", "archived"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`tm-filter-${s}`}
              aria-pressed={filter === s}
              className={`px-3 py-1 text-xs rounded uppercase font-semibold tracking-widest ${
                filter === s ? "bg-[#F26A21] text-[#0B0B0B]" : "text-gray-400 bg-white/5 hover:bg-white/10"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto border border-gray-800 rounded-xl" data-testid="tm-list-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Programa</th>
                <th className="text-left px-3 py-2">Contacto</th>
                <th className="text-left px-3 py-2">Empresa</th>
                <th className="text-left px-3 py-2">Duración</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-right px-3 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Cargando...</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Sin resultados.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-gray-800" data-testid={`tm-row-${r.id}`}>
                  <td className="px-3 py-2 text-white font-semibold">{r.program_name}</td>
                  <td className="px-3 py-2 text-gray-300 text-xs">{r.contact?.name} · {r.contact?.email}</td>
                  <td className="px-3 py-2 text-gray-300 text-xs">{r.contact?.company}</td>
                  <td className="px-3 py-2 font-mono text-xs text-white">{r.desired_duration} · {r.modality}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/app/staff/tailor-made/${r.id}`} data-testid={`tm-open-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#93C5FD] hover:text-white">
                      Abrir <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </StaffLayout>
  );
}
